import React, { useEffect, useMemo, useState } from 'react';
import { fetchData } from '../components/misc';

const DEFAULT_HOST = 'penzi-halisi.com';
const DEFAULT_START_EVENT = 'penzi_gender_gate_selected';
const DEFAULT_END_EVENT = 'penzi_gender_gate_continue_clicked';
const MODAL_EVENTS = ['penzi_gender_gate_modal_shown', 'gender_gate_modal_shown'];
const SIGNUP_EVENTS = ['signup_submit_success', 'signup_registration_success_viewed'];

function eventNamesForJourney(journey) {
  return (journey.actions || []).flatMap(action => action.event_names || []);
}

function hasEvent(journey, eventName) {
  return eventNamesForJourney(journey).includes(eventName);
}

function hasAnyEvent(journey, eventNames) {
  const names = eventNamesForJourney(journey);
  return eventNames.some(eventName => names.includes(eventName));
}

function formatTime(epoch) {
  return epoch ? new Date(epoch * 1000).toLocaleString() : 'Unknown';
}

export default function BridgeStatsDashboard() {
  const [host, setHost] = useState(DEFAULT_HOST);
  const [startEvent, setStartEvent] = useState(DEFAULT_START_EVENT);
  const [endEvent, setEndEvent] = useState(DEFAULT_END_EVENT);
  const [limit, setLimit] = useState(100);
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function loadStats() {
    const params = new URLSearchParams();
    if (host) params.set('host', host);
    if (startEvent) params.set('start_event', startEvent);
    if (endEvent) params.set('end_event', endEvent);
    params.set('limit', String(limit || 100));

    setLoading(true);
    setError('');
    fetchData(`/api/sirri_api/bridge_funnel_actions?${params.toString()}`)
      .then(data => {
        if (data && data.responsecode === 200) {
          setJourneys(data.journeys || []);
        } else {
          setJourneys([]);
          setError(data?.message || 'Unable to load bridge stats.');
        }
      })
      .catch(() => {
        setJourneys([]);
        setError('Unable to load bridge stats.');
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadStats();
  }, []);

  const summary = useMemo(() => {
    const total = journeys.length;
    const started = startEvent ? journeys.filter(journey => hasEvent(journey, startEvent)).length : total;
    const modalShown = journeys.filter(journey => hasAnyEvent(journey, MODAL_EVENTS)).length;
    const completed = endEvent ? journeys.filter(journey => hasEvent(journey, endEvent)).length : journeys.filter(journey => journey.completed).length;
    const signupLinked = journeys.filter(journey => hasAnyEvent(journey, SIGNUP_EVENTS)).length;
    const dropped = Math.max(started - completed, 0);
    const continueRate = started > 0 ? Math.round((completed / started) * 100) : 0;
    return { total, started, modalShown, completed, signupLinked, dropped, continueRate };
  }, [journeys, startEvent, endEvent]);

  const cards = [
    ['Journeys', summary.total],
    ['Started', summary.started],
    ['Modal shown', summary.modalShown],
    ['Continued', summary.completed],
    ['Linked to signup', summary.signupLinked],
    ['Drop-offs', summary.dropped],
    ['Continue rate', `${summary.continueRate}%`],
  ];

  return (
    <div className="w-full max-w-6xl mx-auto text-white">
      <div className="bg-black p-4 rounded mb-4">
        <h1 className="text-2xl mb-2">Bridge Stats</h1>
        <p className="text-sm text-gray-300 mb-4">General funnel stats from bridge visitor/session journeys.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-black">
          <input className="p-2 rounded" value={host} onChange={e => setHost(e.target.value)} placeholder="Host/domain" />
          <input className="p-2 rounded" value={startEvent} onChange={e => setStartEvent(e.target.value)} placeholder="Start event" />
          <input className="p-2 rounded" value={endEvent} onChange={e => setEndEvent(e.target.value)} placeholder="End event" />
          <input className="p-2 rounded" type="number" min="1" max="500" value={limit} onChange={e => setLimit(e.target.value)} placeholder="Limit" />
        </div>
        <button className="mt-3 bg-white text-black px-4 py-2 rounded" onClick={loadStats} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh stats'}
        </button>
        {error ? <p className="text-red-300 text-sm mt-2">{error}</p> : null}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {cards.map(([label, value]) => (
          <div key={label} className="bg-black p-4 rounded">
            <p className="text-xs text-gray-400">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        ))}
      </div>

      <div className="bg-black p-4 rounded">
        <h2 className="text-xl mb-3">Recent Journeys</h2>
        {journeys.length === 0 ? (
          <p className="text-sm">No journeys found for these filters.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {journeys.map((journey, idx) => {
              const events = eventNamesForJourney(journey);
              return (
                <details key={`${journey.visitor_id}-${journey.session_id}-${idx}`} className="border border-gray-700 rounded p-3 text-sm">
                  <summary className="cursor-pointer">
                    <span className="font-bold">{journey.completed ? 'Completed' : 'Open/drop-off'}</span>
                    <span className="text-gray-300"> — {journey.visitor_id} / {journey.session_id} — {formatTime(journey.last_seen_epoch)}</span>
                  </summary>
                  <div className="mt-2 text-xs break-words">
                    <p><strong>First seen:</strong> {formatTime(journey.first_seen_epoch)}</p>
                    <p><strong>Events:</strong> {events.join(' → ') || 'None'}</p>
                    <div className="mt-2 flex flex-col gap-2">
                      {(journey.actions || []).map((action, actionIdx) => (
                        <div key={actionIdx} className="bg-gray-900 p-2 rounded">
                          <p><strong>#{action.event_seq || actionIdx + 1}</strong> {(action.event_names || []).join(', ') || 'bridge_action'} — {formatTime(action.server_received_epoch)}</p>
                          <p>{action.host || ''}{action.path ? ` ${action.path}` : ''}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
